import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiExternalLink,
  FiGithub,
  FiCalendar,
  FiClock,
  FiUser,
} from "react-icons/fi";
import { projectsData } from "../data/projects";
import "./ProjectDetail.css";

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const foundProject = projectsData.find((p) => p.id === parseInt(id));
    setProject(foundProject);
  }, [id]);

  if (!project) {
    return (
      <div className="project-not-found">
        <h2>Project not found</h2>
        <Link to="/" className="btn btn-primary">
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="project-detail">
      {/* Hero Section */}
      <section className="project-hero">
        <div className="container">
          <Link to="/#portfolio" className="back-link">
            <FiArrowLeft />
            Back to Portfolio
          </Link>

          <div className="project-hero-content">
            <span className="project-category">{project.category}</span>
            <h1 className="project-title">{project.title}</h1>
            <p className="project-subtitle">{project.fullDescription}</p>

            <div className="project-meta">
              <div className="meta-item">
                <FiCalendar />
                <span>{project.year}</span>
              </div>
              <div className="meta-item">
                <FiClock />
                <span>{project.duration}</span>
              </div>
              <div className="meta-item">
                <FiUser />
                <span>{project.role}</span>
              </div>
            </div>

            <div className="project-actions">
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                <FiExternalLink />
                Live Demo
              </a>
              <a
                href={project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                <FiGithub />
                View Code
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="project-gallery">
        <div className="container">
          <div className="gallery-main">
            <img
              src={project.images[currentImageIndex]}
              alt={`${project.title} screenshot ${currentImageIndex + 1}`}
            />
          </div>
          <div className="gallery-thumbnails">
            {project.images.map((image, index) => (
              <button
                key={index}
                className={`thumbnail ${currentImageIndex === index ? "active" : ""}`}
                onClick={() => setCurrentImageIndex(index)}
              >
                <img src={image} alt={`Thumbnail ${index + 1}`} />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section className="project-content">
        <div className="container">
          <div className="content-grid">
            {/* Main Content */}
            <div className="main-content">
              {/* Problem */}
              <div className="content-block">
                <h2>The Challenge</h2>
                <p>{project.problem}</p>
              </div>

              {/* Process */}
              <div className="content-block">
                <h2>My Approach</h2>
                <p>{project.process}</p>
              </div>

              {/* Results */}
              <div className="content-block">
                <h2>Results & Impact</h2>
                <ul className="results-list">
                  {project.results.map((result, index) => (
                    <li key={index}>
                      <span className="result-icon">✓</span>
                      {result}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Sidebar */}
            <div className="sidebar">
              <div className="sidebar-card">
                <h3>Project Info</h3>
                <div className="info-item">
                  <span className="info-label">Role</span>
                  <span className="info-value">{project.role}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Duration</span>
                  <span className="info-value">{project.duration}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Year</span>
                  <span className="info-value">{project.year}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Category</span>
                  <span className="info-value">{project.category}</span>
                </div>
              </div>

              <div className="sidebar-card">
                <h3>Technologies</h3>
                <div className="tech-tags">
                  {project.technologies.map((tech, index) => (
                    <span key={index} className="tech-tag">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Next Project */}
      <section className="next-project">
        <div className="container">
          <h2>Continue Exploring</h2>
          <div className="next-project-grid">
            {projectsData
              .filter((p) => p.id !== project.id)
              .slice(0, 3)
              .map((p) => (
                <Link
                  to={`/project/${p.id}`}
                  key={p.id}
                  className="next-project-card"
                >
                  <div className="next-project-image">
                    <img src={p.thumbnail} alt={p.title} />
                  </div>
                  <div className="next-project-info">
                    <span className="next-project-category">{p.category}</span>
                    <h3>{p.title}</h3>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
